if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        // Shop Pay / accelerated checkout bypasses our submit handler; intercept in capture phase so gift + embroidery fees run first.
        this.form.addEventListener('click', this.onAcceleratedCheckoutClickCapture.bind(this), true);
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      isCardInlineVariantBlocked() {
        const cardInline = this.form.closest('card-inline-variants');
        return !!(cardInline && !cardInline.dataset.singleOptionColor);
      }

      hasPostCartExtras() {
        const embroideryAdded = document.querySelector('[data-embroidery-options-added]');
        const embroideryActive =
          embroideryAdded &&
          embroideryAdded.style.display !== 'none' &&
          embroideryAdded.getAttribute('data-embroidery-data');

        const giftSummary = document.querySelector('[data-gift-summary]');
        let giftActive = false;
        if (giftSummary) {
          const giftItemsAttr = giftSummary.getAttribute('data-gift-items');
          if (giftItemsAttr) {
            try {
              const giftItems = JSON.parse(giftItemsAttr);
              giftActive = !!(giftItems && giftItems.length > 0);
            } catch (e) {
              /* ignore */
            }
          }
        }

        return !!(embroideryActive || giftActive);
      }

      isAcceleratedCheckoutClick(event) {
        const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
        const inThisForm = path.some((el) => el && el.nodeType === 1 && this.form.contains(el));
        if (!inThisForm) return false;
        return path.some(
          (el) =>
            el &&
            el.nodeType === 1 &&
            (el.tagName === 'SHOPIFY-ACCELERATED-CHECKOUT' ||
              el.tagName === 'SHOPIFY-ACCELERATED-CHECKOUT-CART' ||
              el.classList?.contains('shopify-payment-button'))
        );
      }

      async checkAndAddEmbroideryFee(cartData) {
        const embroideryAddedContainer = document.querySelector('[data-embroidery-options-added]');
        if (!embroideryAddedContainer || embroideryAddedContainer.style.display === 'none') {
          return { cartData, embroideryFailed: false };
        }
        const embroideryDataAttr = embroideryAddedContainer.getAttribute('data-embroidery-data');
        if (!embroideryDataAttr) {
          return { cartData, embroideryFailed: false };
        }
        try {
          const embroideryData = JSON.parse(embroideryDataAttr);
          if (!embroideryData || !embroideryData.feeVariantId) {
            return { cartData, embroideryFailed: false };
          }
          const result = await this.addEmbroideryFeeToCart(embroideryData, cartData);

          if (result === cartData || result.embroideryFailed) {
            await this.rollbackEmbroideryFromMainProduct(cartData, embroideryData.embroideryId);
            this.showEmbroideryError();
            this.resetEmbroideryDisplay();
            return { cartData, embroideryFailed: true };
          }
          return { cartData: result, embroideryFailed: false };
        } catch (e) {
          console.error('Error parsing embroidery data:', e);
          return { cartData, embroideryFailed: false };
        }
      }

      renderCartDrawerAfterAdd(cartData) {
        const quickAddModal = this.closest('quick-add-modal');
        if (quickAddModal) {
          document.body.addEventListener(
            'modalClosed',
            () => {
              setTimeout(() => {
                CartPerformance.measure('add:paint-updated-sections', () => {
                  this.cart.renderContents(cartData);
                });
                setTimeout(() => {
                  window.cartJustUpdated = false;
                }, 1000);
              });
            },
            { once: true }
          );
          quickAddModal.hide(true);
        } else {
          CartPerformance.measure('add:paint-updated-sections', () => {
            this.cart.renderContents(cartData);
          });
          setTimeout(() => {
            window.cartJustUpdated = false;
          }, 1000);
        }
      }

      async handleSuccessfulCartAdd(response, formData, options) {
        const { target } = options;

        const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
        if (!this.error) {
          await publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'product-form',
            productVariantId: formData.get('id'),
            cartData: response,
          }).then(() => {
            CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
          });
        }
        this.error = false;

        let workingCart = response;
        const giftSummary = document.querySelector('[data-gift-summary]');
        if (giftSummary) {
          const giftItemsAttr = giftSummary.getAttribute('data-gift-items');
          const giftId = giftSummary.getAttribute('data-gift-id');
          const productTitle = giftSummary.getAttribute('data-product-title') || '';
          const giftMessage = giftSummary.getAttribute('data-gift-message') || '';
          if (giftItemsAttr) {
            try {
              const giftItems = JSON.parse(giftItemsAttr);
              if (giftItems && giftItems.length > 0) {
                try {
                  workingCart =
                    (await this.addGiftProductsToCart(giftItems, response, giftId, productTitle, giftMessage)) ||
                    response;
                } catch (error) {
                  console.error('Error adding gift products to cart:', error);
                  workingCart = response;
                }
              }
            } catch (e) {
              console.error('Error parsing gift variant IDs:', e);
            }
          }
        }

        const { cartData: finalCart, embroideryFailed } = await this.checkAndAddEmbroideryFee(workingCart);

        if (target === 'checkout') {
          if (embroideryFailed) {
            if (this.cart) {
              this.renderCartDrawerAfterAdd(finalCart);
            }
            setTimeout(() => {
              window.cartJustUpdated = false;
            }, 1000);
            return;
          }
          window.location.href = window.routes.checkout_url || '/checkout';
          return;
        }

        this.renderCartDrawerAfterAdd(finalCart);
      }

      async onAcceleratedCheckoutClickCapture(e) {
        if (!this.hasPostCartExtras()) return;
        if (!this.isAcceleratedCheckoutClick(e)) return;
        if (this.isCardInlineVariantBlocked()) return;
        if (this._acceleratedCheckoutInProgress) return;
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        e.preventDefault();
        e.stopImmediatePropagation();
        e.stopPropagation();

        this._acceleratedCheckoutInProgress = true;
        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        window.cartJustUpdated = true;

        try {
          const config = fetchConfig('javascript');
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          delete config.headers['Content-Type'];

          const formData = new FormData(this.form);
          if (this.cart) {
            formData.append(
              'sections',
              this.cart.getSectionsToRender().map((section) => section.id)
            );
            formData.append('sections_url', window.location.pathname);
            this.cart.setActiveElement(document.activeElement);
          }
          config.body = formData;

          const res = await fetch(`${routes.cart_add_url}`, config);
          const response = await res.json();

          if (response.status) {
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'product-form',
              productVariantId: formData.get('id'),
              errors: response.errors || response.description,
              message: response.message,
            });
            this.handleErrorMessage(response.description);

            const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
            if (soldOutMessage) {
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
            }
            return;
          }

          this.error = false;
          await this.handleSuccessfulCartAdd(response, formData, { target: 'checkout' });
        } catch (err) {
          console.error(err);
          window.cartJustUpdated = false;
        } finally {
          this._acceleratedCheckoutInProgress = false;
          this.submitButton.classList.remove('loading');
          if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
          if (!this.error) this.submitButton.removeAttribute('aria-disabled');
          this.querySelector('.loading__spinner').classList.add('hidden');
        }
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        if (this.isCardInlineVariantBlocked()) return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        window.cartJustUpdated = true;

        try {
          const res = await fetch(`${routes.cart_add_url}`, config);
          const response = await res.json();

          if (response.status) {
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'product-form',
              productVariantId: formData.get('id'),
              errors: response.errors || response.description,
              message: response.message,
            });
            this.handleErrorMessage(response.description);

            const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
            if (!soldOutMessage) return;
            this.submitButton.setAttribute('aria-disabled', true);
            this.submitButtonText.classList.add('hidden');
            soldOutMessage.classList.remove('hidden');
            this.error = true;
            return;
          } else if (!this.cart) {
            window.location = window.routes.cart_url;
            return;
          }

          this.error = false;
          await this.handleSuccessfulCartAdd(response, formData, { target: 'drawer' });
        } catch (e) {
          console.error(e);
          window.cartJustUpdated = false;
        } finally {
          this.submitButton.classList.remove('loading');
          if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
          if (!this.error) this.submitButton.removeAttribute('aria-disabled');
          this.querySelector('.loading__spinner').classList.add('hidden');

          CartPerformance.measureFromEvent('add:user-action', evt);
        }
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        const priceSpan = this.submitButtonText.querySelector('.button-price');
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) {
            // Hide price when disabled and show status text
            if (priceSpan) priceSpan.style.display = 'none';
            this.submitButtonText.childNodes[0].textContent = text;
          }
        } else {
          this.submitButton.removeAttribute('disabled');
          // Show "Add to Cart" text and restore price display
          this.submitButtonText.childNodes[0].textContent = window.variantStrings.addToCart + ' ';
          if (priceSpan) priceSpan.style.display = '';
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }

      async addGiftProductsToCart(giftItems, initialCartData, giftId, productTitle = '', giftMessage = '') {
        // Add each gift product to the cart sequentially
        let currentCartData = initialCartData;
        
        for (const giftItem of giftItems) {
          const config = fetchConfig('javascript');
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          delete config.headers['Content-Type'];
          
          const formData = new FormData();
          formData.append('id', giftItem.id);
          formData.append('quantity', '1');
          
          // Add gift ID, mark as gift product, include gift type, Gift For (product name), and Gift Message
          if (giftId) {
            formData.append('properties[_Gift ID]', giftId);
            formData.append('properties[Gift Product]', 'true');
            formData.append('properties[Gift Type]', giftItem.type); // 'option', 'wrap', or 'receipt'
            if (productTitle) {
              formData.append('properties[Gift For]', productTitle);
            }
            if (giftMessage && giftMessage.trim()) {
              formData.append('properties[Gift Message]', giftMessage.trim());
            }
          }
          
          if (this.cart) {
            formData.append(
              'sections',
              this.cart.getSectionsToRender().map((section) => section.id)
            );
            formData.append('sections_url', window.location.pathname);
          }
          
          config.body = formData;
          
          try {
            const response = await fetch(`${routes.cart_add_url}`, config);
            const responseData = await response.json();
            
            if (responseData.status) {
              console.error('Error adding gift product to cart:', responseData.description);
              // Continue with other products even if one fails
              continue;
            }
            
            // Update current cart data with the latest response
            currentCartData = responseData;
            
            // Publish cart update event for each gift product
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-form',
              productVariantId: giftItem.id,
              cartData: responseData,
            });
          } catch (error) {
            console.error('Error adding gift product to cart:', error);
            // Continue with other products even if one fails
            continue;
          }
        }
        
        return currentCartData;
      }

      async addEmbroideryFeeToCart(embroideryData, initialCartData) {
        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];
        
        const formData = new FormData();
        formData.append('id', embroideryData.feeVariantId);
        formData.append('quantity', '1');
        
        // Add embroidery properties
        formData.append('properties[_Embroidery ID]', embroideryData.embroideryId);
        formData.append('properties[embroideryID]', embroideryData.embroideryId);
        formData.append('properties[Embroidery For]', embroideryData.productTitle);
        
        if (embroideryData.type) formData.append('properties[Embroidery Type]', embroideryData.type);
        if (embroideryData.location) formData.append('properties[Location]', embroideryData.location);
        if (embroideryData.font) formData.append('properties[Font]', embroideryData.font);
        if (embroideryData.color) formData.append('properties[Font Color]', embroideryData.color);
        
        // Handle design property based on type
        if (embroideryData.design) {
          const typeLower = embroideryData.type ? embroideryData.type.toLowerCase() : '';
          if (typeLower.includes('hand scripted')) {
            formData.append('properties[Hand Scripted Design]', embroideryData.design);
          } else if (typeLower.includes('sports package')) {
            formData.append('properties[Sports Design]', embroideryData.design);
          } else {
            formData.append('properties[Design]', embroideryData.design);
          }
        }
        
        if (embroideryData.monogramStyle) formData.append('properties[Monogram Style]', embroideryData.monogramStyle);
        if (embroideryData.text) formData.append('properties[Text]', embroideryData.text);
        if (embroideryData.pawDesignLabel) formData.append('properties[Paw Design]', embroideryData.pawDesignLabel);
        
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
        }
        
        config.body = formData;
        
        try {
          const response = await fetch(`${routes.cart_add_url}`, config);
          const responseData = await response.json();
          
          if (responseData.status) {
            console.error('Error adding embroidery fee to cart:', responseData.description);
            return { ...initialCartData, embroideryFailed: true };
          }
          
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'product-form',
            productVariantId: embroideryData.feeVariantId,
            cartData: responseData,
          });
          
          // Add paw design product if selected (variant 42919495893191 or from metafield)
          if (embroideryData.pawDesignVariantId) {
            const pawConfig = fetchConfig('javascript');
            pawConfig.headers['X-Requested-With'] = 'XMLHttpRequest';
            delete pawConfig.headers['Content-Type'];
            const pawFormData = new FormData();
            pawFormData.append('id', embroideryData.pawDesignVariantId);
            pawFormData.append('quantity', '1');
            pawFormData.append('properties[_Embroidery ID]', embroideryData.embroideryId);
            pawFormData.append('properties[embroideryID]', embroideryData.embroideryId);
            pawFormData.append('properties[Embroidery For]', embroideryData.productTitle);
            if (embroideryData.pawDesignLabel) {
              pawFormData.append('properties[Paw Design]', embroideryData.pawDesignLabel);
            }
            if (this.cart) {
              pawFormData.append(
                'sections',
                this.cart.getSectionsToRender().map((section) => section.id)
              );
              pawFormData.append('sections_url', window.location.pathname);
            }
            pawConfig.body = pawFormData;
            try {
              const pawResponse = await fetch(`${routes.cart_add_url}`, pawConfig);
              const pawResponseData = await pawResponse.json();
              if (pawResponseData.status) {
                console.error('Error adding paw design product to cart:', pawResponseData.description || pawResponseData.message);
              } else {
                publish(PUB_SUB_EVENTS.cartUpdate, {
                  source: 'product-form',
                  productVariantId: embroideryData.pawDesignVariantId,
                  cartData: pawResponseData,
                });
              }
            } catch (pawError) {
              console.error('Error adding paw design to cart:', pawError);
            }
          }
          
          // Return full cart so drawer shows main product + fee + paw (if added)
          try {
            const cartResponse = await fetch(`${routes.cart_url}.js`);
            const fullCart = await cartResponse.json();
            return fullCart;
          } catch (e) {
            return responseData;
          }
        } catch (error) {
          console.error('Error adding embroidery fee to cart:', error);
          return { ...initialCartData, embroideryFailed: true };
        }
      }

      // Rollback embroidery from main product when fee fails to add
      async rollbackEmbroideryFromMainProduct(cartData, embroideryId) {
        try {
          // Fetch current cart to find the main product
          const cartResponse = await fetch(`${routes.cart_url}.js`);
          const currentCart = await cartResponse.json();
          
          // Find the main product with this Embroidery ID (hidden property key)
          const mainProductIndex = currentCart.items.findIndex((item) => {
            if (!item.properties) return false;
            const id =
              item.properties['_Embroidery ID'] ??
              item.properties['embroideryID'] ??
              item.properties['Embroidery ID'];
            return id === embroideryId;
          });
          
          if (mainProductIndex === -1) return;
          
          const mainProduct = currentCart.items[mainProductIndex];
          
          // Copy all properties except Embroidery ID (hidden, visible, and legacy keys)
          const updatedProperties = {};
          if (mainProduct.properties) {
            Object.keys(mainProduct.properties).forEach((key) => {
              if (key !== '_Embroidery ID' && key !== 'embroideryID' && key !== 'Embroidery ID') {
                updatedProperties[key] = mainProduct.properties[key];
              }
            });
          }

          // Update main product to clear Embroidery ID
          await fetch(`${routes.cart_change_url}`, {
            ...fetchConfig(),
            body: JSON.stringify({
              id: mainProduct.key,
              properties: updatedProperties
            })
          });
        } catch (error) {
          console.error('Error rolling back embroidery from main product:', error);
        }
      }

      // Show embroidery error message
      showEmbroideryError() {
        // Create and show error message
        const errorContainer = document.querySelector('[data-embroidery-error]') || this.createEmbroideryErrorElement();
        if (errorContainer) {
          errorContainer.textContent = 'There was an issue with embroidery. Please try again.';
          errorContainer.style.display = 'block';
          
          // Auto-hide after 5 seconds
          setTimeout(() => {
            errorContainer.style.display = 'none';
          }, 5000);
        }
      }

      // Create embroidery error element if it doesn't exist
      createEmbroideryErrorElement() {
        const embroideryContainer = document.querySelector('[data-embroidery-options-added]')?.closest('.gift-options');
        if (!embroideryContainer) return null;
        
        const errorDiv = document.createElement('div');
        errorDiv.setAttribute('data-embroidery-error', '');
        errorDiv.className = 'gift-options__error';
        errorDiv.style.cssText = 'color: #c00; padding: 10px; margin: 10px 0; background: #fee; border-radius: 4px; display: none;';
        embroideryContainer.insertBefore(errorDiv, embroideryContainer.firstChild);
        return errorDiv;
      }

      // Reset embroidery display to initial state
      resetEmbroideryDisplay() {
        const addedContainer = document.querySelector('[data-embroidery-options-added]');
        const initialContainer = document.querySelector('[data-embroidery-options-initial]');
        
        if (addedContainer) {
          addedContainer.style.display = 'none';
          addedContainer.removeAttribute('data-embroidery-data');
        }
        
        if (initialContainer) {
          initialContainer.style.display = 'block';
        }
        
        // Also clear the Embroidery ID from the product form (hidden + legacy keys)
        const form = document.querySelector('product-form form') || document.querySelector('form[action*="/cart/add"]');
        if (form) {
          form
            .querySelectorAll(
              'input[name="properties[_Embroidery ID]"], input[name="properties[embroideryID]"], input[name="properties[Embroidery ID]"]'
            )
            .forEach((el) => el.remove());
        }
      }
    }
  );
}
